import { useState, useEffect } from "react";
import Input from "../../../../../components/fields/input";
import Button from "../../../../../components/buttons/button";
import {
  useCreateNewsMutation,
  useUpdateNewsMutation,
  // Import the createFile mutation
} from "../../../../../store/Reducer/news";
import toast from "react-hot-toast";
import { withHttps } from "../../../../../utils/links";
import { useCreateFileMutation } from "../../../../../store/Reducer/file";
import { useTranslation } from "react-i18next";

export default function CreateNewsModal({
  isOpen,
  closeModal,
  isEdit,
  rowData,
}) {
  const { t } = useTranslation();
  const [createNews, { isLoading }] = useCreateNewsMutation();
  const [updateNews, { isLoading: newsupdating }] = useUpdateNewsMutation();
  const [createFile, { isLoading: fileLoading }] = useCreateFileMutation();
  const [filePreview, setFilePreview] = useState(null);
  console.log("isEdit", isEdit);

  const [formData, setFormData] = useState({
    title: "",
    details: "",
    file: null,
    links: [""], // Start with one empty link
    isActive: true, // Default to active
    isPublished: false,
  });

  useEffect(() => {
    if (isOpen && isEdit && rowData) {
      setFormData({
        title: rowData.title || "",
        details: rowData.details || "",
        file: rowData?.file,
        links: rowData.links || [""],
        isActive: rowData.isActive ,
        isPublished: rowData.isPublished,
      });
    }
  }, [isOpen, isEdit, rowData]);

  const handleChange = (e) => {
    const { name, value, type, files, checked } = e.target;

    // Handle file input separately to create an image preview
    if (type === "file") {
      const file = files[0];
      setFormData((prev) => ({
        ...prev,
        [name]: file,
      }));

      // Generate the preview URL for the selected file
      if (file) {
        const previewUrl = URL.createObjectURL(file);
        setFilePreview(previewUrl); // Store the preview URL
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  useEffect(() => {
    // If editing, set the filePreview to the existing file URL from rowData
    if (isEdit && rowData?.file?.url) {
      setFilePreview(rowData?.file?.url);
    }
  }, [isEdit, rowData]);

  const handleLinkChange = (index, value) => {
    const updatedLinks = [...formData.links];
    updatedLinks[index] = value;
    setFormData((prev) => ({
      ...prev,
      links: updatedLinks,
    }));
  };
  const toggleActive = () => {
    setFormData((prev) => ({
      ...prev,
      isActive: !prev.isActive,
    }));
  };



  const resetForm = () => {
    setFormData({
      title: "",
      details: "",
      file: null,
      links: [""], // Start with one empty link
      isActive: true, // Default to active
      isPublished: false,
    });
    setFilePreview(null); // Clear file preview
  };

  const handleSubmit = async (e, isPublish) => {
    e.preventDefault();
    setFormData((prev) => ({ ...prev, isPublished: isPublish }));

    let fileData = null;
    if (formData.file && formData.file instanceof File) {
      try {
        const fileResponse = await createFile(formData.file).unwrap();
        fileData = {
          name: fileResponse?.data?.fileName || formData.file.name,
          url: fileResponse?.data?.fileUrl || formData.file.name,
        };
      } catch (error) {
        toast.error(
          "File upload failed: " + error?.data?.message || error.toString()
        );
        return;
      }
    }

    const payload = {
      title: formData.title,
      details: formData.details,
      file: fileData || formData.file,
      links: [withHttps(formData.links[0])].filter(Boolean),
      // Publishing decides what shows; there is no separate on/off.
      isActive: true,
      isPublished: formData?.isPublished,
    };

    if (isEdit && rowData?._id) {
      payload.id = rowData._id;
    }

    try {
      let result;
      if (isEdit) {
        result = await updateNews(payload).unwrap();
      } else {
        result = await createNews(payload).unwrap();
      }

      toast.success(
        isEdit ? "News updated successfully!" : "News created successfully!"
      );
      resetForm();
      closeModal();
    } catch (error) {
      console.error("Error handling the news operation:", error);
      toast.error(error?.data?.message || "An error occurred");
    }
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 overflow-y-auto bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 sm:p-0" onClick={closeModal}>
          <div className="rounded-lg shadow-xl w-full max-w-3xl mx-auto h-full sm:h-auto sm:max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto w-full bg-white rounded-lg shadow-lg p-4 sm:px-6 sm:py-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 mb-6">
                  {isEdit ? t("news_create_modal_title_edit_news") : t("news_create_modal_title_post_a_news")}
                </h1>

                {(isLoading || fileLoading || newsupdating) && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <div className="loader border-t-4 border-blue-500 rounded-full w-16 h-16 animate-spin"></div>
                      <p className="text-white mt-4">Loading...</p>
                    </div>
               )}

                {filePreview && (
                  <div className="my-4">
                    <img
                      src={filePreview} // Display the preview URL here
                      alt="Preview"
                      className="w-24 h-auto mt-2 rounded"
                    />
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* News Title */}
                  <Input
                    label="News Title"
                    type="text"
                    name="title"
                    placeholder="Enter News Title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />

                  {/* News Text */}
                  <div className="w-full">
                    <textarea
                      id="newsText"
                      name="details"
                      rows="4"
                      className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-400 focus:ring-blue-500 focus:border-blue-500 focus:outline-none placeholder:text-gray-500"
                      placeholder="Enter your news details here..."
                      value={formData.details}
                      onChange={handleChange}
                      required

                    ></textarea>
                  </div>

                  {/* Upload Image */}
                  <div className="w-full">
                    <label htmlFor="image" className="sr-only">
                      Choose file
                    </label>
                    <input
                      type="file"
                      name="file"
                      id="image"
                      accept="image/*"
                      onChange={handleChange}
                      className="block w-full border border-gray-300 shadow-sm rounded-lg text-sm focus:z-10 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-400 file:bg-gray-50 file:border-0 file:me-4 file:py-3 file:px-4"
                      required={!isEdit}
                    />
                  </div>

                  {/* "Read more" link: where the news item's button on the website goes. */}
                  <div className="space-y-1">
                    <label htmlFor="readMoreLink" className="block text-sm font-medium text-gray-700">
                      Read more link (optional)
                    </label>
                    <input
                      id="readMoreLink"
                      type="text"
                      inputMode="url"
                      className="w-full p-2 border border-gray-400 rounded-lg focus:ring-blue-500 focus:border-blue-500 focus:outline-none placeholder:text-sm placeholder:text-gray-500"
                      placeholder="e.g. yourmasjid.org/full-story"
                      value={formData.links[0] || ""}
                      onChange={(e) => handleLinkChange(0, e.target.value)}
                      onBlur={(e) => handleLinkChange(0, withHttps(e.target.value))}
                    />
                    <p className="text-xs text-gray-500">Shown as a "Read more" button on your website.</p>
                  </div>

                  {/* Submit Button */}

                  <div className="flex justify-center sm:justify-end items-center flex-wrap space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 rounded-lg font-semibold text-sm transition-colors duration-200 ease-in-out focus:outline-none flex items-center text-gray-800 border border-gray-700 hover:bg-gray-100 justify-center"
                    >
                      {t("universal_cancel_text")}
                    </button>

                    <Button
                      text={t("universal_save_text")}
                      type="submit"
                      className="px-6 text-nowrap"
                    />

                    <div className="flex items-center space-x-3">
                      


                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="isPublished"
                          id="isPublished"
                          checked={formData.isPublished}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, isPublished: e.target.checked }))
                          }
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label
                          htmlFor="isPublished"
                          className="ml-2 text-sm text-gray-700 cursor-pointer"
                        >
                          Publish on your website
                        </label>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
